import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Route, Switch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import * as routing from '../../constants/routes'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { useUpdateExternalGMS } from '../../../hooks/use-external-gms'
import CaptureGMSSelection from './CaptureGMSSelection'
import { FormValues } from './CaptureGMSForm'
import { OnboardingStepKey } from '@cbgms/api'
import { CaptureGMSFormCTA } from './CaptureGMSFormCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (formValues: FormValues) => void
}

export const useCaptureGMSInformationStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const dispatch = useDispatch()
  const [externalGMS, setExternalGMS] = useState<FormValues>()
  const stepName = OnboardingStepKey.ExternalGMS
  const updateExternalGMS = useUpdateExternalGMS()
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const onStepSubmit = (values: FormValues) => {
    setExternalGMS(values)
  }
  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    await updateExternalGMS.mutateAsync({ ExternalGMSUUID: externalGMS?.UUID || '', Remark: externalGMS?.Name || '' })
  }, [externalGMS, dispatch, updateExternalGMS])

  return {
    Component: CaptureGMSInformationStep,
    CTAComponent: CaptureGMSFormCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit
    },
    persister: {
      onPersist,
      persistOrder: 5
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.capture_gms_information.stepper_text')}</>
}

export const CaptureGMSInformationStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, ...rest }) => {
  return (
    <Switch>
      <Route path={routing.pathCaptureGMSSelection(basePath)} render={() => <CaptureGMSSelection {...rest} />} />
    </Switch>
  )
}
